import { useState } from "react";
import { ArrowLeft, MailCheck, KeyRound, CheckCircle2 } from "lucide-react";
import { inputCls, btnPrimary } from "./crm";
import { errMsg } from "./useCrm";
import { forgotPassword, resetPassword } from "./authApi";

export function ForgotPasswordForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { setSent((await forgotPassword(email)).message); } catch (err) { setError(errMsg(err, "Could not send the reset link. Try again.")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="forgot-password-view">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0b1528] text-white"><KeyRound className="h-5 w-5" /></span>
      <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-600">Password recovery</div>
      <h1 className="mt-1 text-2xl font-extrabold text-slate-900">Forgot your password?</h1>
      <p className="mt-1.5 text-sm text-slate-500">Enter the e-mail on your account and we'll send a link to set a new one.</p>
      {sent ? (
        <div className="mt-7 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="forgot-password-sent">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><MailCheck className="h-4 w-4" /> Check your inbox</div>
          <p className="mt-1 text-xs text-emerald-700">{sent}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="forgot-password-form">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Email address</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email" className={inputCls} placeholder="you@company.com" />
          </div>
          {error && <p data-testid="forgot-password-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="forgot-password-submit" className={`${btnPrimary} w-full py-3`}>{busy ? "Sending…" : "E-mail me a reset link"}</button>
        </form>
      )}
      <button onClick={onBack} data-testid="forgot-password-back" className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> Back to sign in</button>
    </div>
  );
}

export function ResetPasswordForm({ token, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== pw2) return setError("The two passwords do not match.");
    setBusy(true);
    try { setDone(await resetPassword(token, pw)); } catch (err) { setError(errMsg(err, "This reset link is invalid or has expired.")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="reset-password-view">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0b1528] text-white"><KeyRound className="h-5 w-5" /></span>
      <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-600">Password recovery</div>
      <h1 className="mt-1 text-2xl font-extrabold text-slate-900">Choose a new password</h1>
      {done ? (
        <div className="mt-7 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="reset-password-done">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Password updated</div>
          <p className="mt-1 text-xs text-emerald-700">Sign in with <b>{done.email}</b> and your new password.</p>
          <button onClick={() => onDone(done)} data-testid="reset-password-continue" className={`${btnPrimary} mt-4 w-full py-3`}>Continue to sign in</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-password-form">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">New password</label>
            <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-new" className={inputCls} placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Confirm new password</label>
            <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" className={inputCls} placeholder="Repeat it" />
          </div>
          {error && <p data-testid="reset-password-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-password-submit" className={`${btnPrimary} w-full py-3`}>{busy ? "Saving…" : "Save new password"}</button>
          <button type="button" onClick={() => onDone(null)} data-testid="reset-password-cancel" className="block w-full text-center text-xs font-semibold text-slate-500 hover:underline">Cancel</button>
        </form>
      )}
    </div>
  );
}
